import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Table,
  Tag,
  Space,
  Typography,
  Button,
  Tooltip,
  Badge,
  theme,
  Input,
  Modal,
  Progress,
} from 'antd';
import {
  MailOutlined,
  PhoneOutlined,
  FileTextOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { Link } from '@tanstack/react-router';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { 
  useJobModuleApplicationsQuery, 
  useDeleteApplicationMutation,
  APPLICATIONS_LARGE_LIMIT,
  type JobModuleApplication, 
  type JobModuleFile 
} from '../api';
import { MODULE_BASE_ROUTE } from '../index';
import { FileListModal, type FileItem } from './FileListModal';
import { useAppointments } from '@/contexts/AppointmentContext';
import useAppNotification from '@/hooks/useAppNotification';
import { useAppointmentsByContextId } from '@/hooks/useAppointmentsByContext';
import { AppointmentScheduleButton } from '@/components/shared/appointments';
import { ErrorAlert } from '@/components/shared/ErrorAlert';
import { DateTimeCell } from '@/components/shared/DateTimeCell';
import { RowActionsMenu } from '@/components/shared/RowActionsMenu';
import { getScoreColor } from '@/utils/score';
import { useResponsivePinnedColumn } from '@/hooks/useResponsivePinnedColumn';

const { Text } = Typography;
const { useToken } = theme;

interface ApplicationsTableProps {
  // No props needed - component is self-contained
}

export function ApplicationsTable({}: ApplicationsTableProps) {
  const { token } = useToken();
  const { t } = useTranslation('job-module');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [resumeModalOpen, setResumeModalOpen] = useState(false);
  const [selectedResumeFiles, setSelectedResumeFiles] = useState<FileItem[]>([]);
  const [attachmentsModalOpen, setAttachmentsModalOpen] = useState(false);
  const [selectedAttachmentFiles, setSelectedAttachmentFiles] = useState<FileItem[]>([]);
  const buttonRefs = useRef<Record<string, HTMLElement>>({});

  const { showSuccessMessage, showErrorMessage } = useAppNotification();
  const { openCreate, openDetails } = useAppointments();

  // Fetch all data with large limit to use cache
  const {
    data: applicationsData,
    isLoading: loading,
    error,
    refetch: refetchApplications
  } = useJobModuleApplicationsQuery({
    limit: APPLICATIONS_LARGE_LIMIT,
    offset: 0
  });

  // Delete mutation
  const deleteApplicationMutation = useDeleteApplicationMutation();

  // Client-side search and pagination (soft pagination)
  const allApplications = applicationsData?.applications || [];

  const applicationIds = useMemo(
    () => allApplications.map((application) => application.id),
    [allApplications]
  );

  const { byId: applicationsWithAppointments } = useAppointmentsByContextId({
    source: 'job-module',
    idKey: 'applicationId',
    ids: applicationIds,
    enabled: applicationIds.length > 0,
  });
  
  // Filter applications based on search text
  const filteredApplications = useMemo(() => {
    if (!searchText.trim()) {
      return allApplications;
    }
    
    const searchLower = searchText.toLowerCase().trim();
    return allApplications.filter((app) => {
      return (
        app.name.toLowerCase().includes(searchLower) ||
        app.email.toLowerCase().includes(searchLower) ||
        (app.phone && app.phone.toLowerCase().includes(searchLower)) ||
        app.position.toLowerCase().includes(searchLower) ||
        (app.message && app.message.toLowerCase().includes(searchLower))
      );
    });
  }, [allApplications, searchText]);

  // Paginate filtered results
  const paginatedApplications = useMemo(() => {
    return filteredApplications.slice(
      (page - 1) * pageSize,
      page * pageSize
    );
  }, [filteredApplications, page, pageSize]);

  const totalCount = filteredApplications.length;

  // Reset to first page when search changes
  useEffect(() => {
    setPage(1);
  }, [searchText]);

  const handlePageChange = (newPage: number, size?: number) => {
    setPage(newPage);
    if (size !== undefined && size !== pageSize) {
      setPageSize(size);
      setPage(1);
    }
  };

  const handleCreateAppointment = (application: JobModuleApplication) => {
    openCreate({
      moduleId: 'job-module',
      moduleData: { application },
      title: t('appointment.createTitle', { name: application.name }),
      initialValues: {
        anonymousName: application.name,
        anonymousPhone: application.phone,
        anonymousEmail: application.email,
        notes: t('appointment.interviewNotes', { position: application.position }),
      },
      onSuccess: async () => {
        showSuccessMessage(t('appointment.createSuccess'));
        await refetchApplications();
      },
      onError: (error) => {
        showErrorMessage(t('appointment.createError', { message: error.message }));
      },
    });
  };

  /**
   * Handle showing appointment details popup
   * Uses the appointment directly from the map (no conversion needed)
   */
  const handleShowAppointmentDetails = (application: JobModuleApplication, event: React.MouseEvent<HTMLElement>) => {
    const appointment = applicationsWithAppointments.get(application.id);
    if (!appointment) return;

    buttonRefs.current[application.id] = event.currentTarget;
    openDetails(appointment, event.currentTarget, { popupZIndex: 1050 });
  };

  const handleOpenResumeModal = (resumeFiles: JobModuleFile[]) => {
    setSelectedResumeFiles(resumeFiles);
    setResumeModalOpen(true);
  };

  const handleOpenAttachmentsModal = (attachmentFiles: JobModuleFile[]) => {
    setSelectedAttachmentFiles(attachmentFiles);
    setAttachmentsModalOpen(true);
  };

  const handleDeleteApplication = (application: JobModuleApplication) => {
    Modal.confirm({
      title: t('applications.deleteConfirmTitle'),
      icon: <ExclamationCircleOutlined />,
      content: t('applications.deleteConfirmDescription', { name: application.name }),
      okText: t('applications.deleteConfirmOk'),
      cancelText: t('applications.deleteConfirmCancel'),
      okButtonProps: { danger: true },
      onOk: () =>
        new Promise<void>((resolve, reject) => {
          deleteApplicationMutation.mutate(application.id, {
            onSuccess: () => {
              showSuccessMessage(t('applications.deleteSuccess', { name: application.name }));
              resolve();
            },
            onError: (err) => {
              showErrorMessage(
                t('applications.deleteError', {
                  message: err instanceof Error ? err.message : 'Unknown error',
                }),
              );
              reject();
            },
          });
        }),
    });
  };

  const fileIconBtn = (
    count: number,
    color: string,
    onClick: () => void,
    tooltipLabel: string,
  ) => (
    <Tooltip title={tooltipLabel}>
      <Badge count={count} showZero={false} size="small">
        <Button
          type="text"
          icon={<FileTextOutlined />}
          onClick={onClick}
          size="small"
          style={{ color, padding: '0 4px', height: 'auto' }}
        />
      </Badge>
    </Tooltip>
  );

  const pinColumn = useResponsivePinnedColumn();
  const columns: ColumnsType<JobModuleApplication> = [
    {
      title: t('applications.columns.name'),
      key: 'name',
      width: 150,
      render: (_, record) => (
        <Space size={4}>
          <Text strong>{record.name}</Text>
          {applicationsWithAppointments.has(record.id) && (
            <Tooltip title={t('applications.appointmentScheduled')}>
              <CheckCircleOutlined style={{ color: token.colorSuccess, fontSize: 12 }} />
            </Tooltip>
          )}
        </Space>
      ),
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: t('applications.columns.contact'),
      key: 'contact',
      width: 200,
      render: (_, record) => (
        <div style={{ lineHeight: 1.6 }}>
          <div>
            <MailOutlined style={{ color: token.colorTextSecondary, marginRight: 4, fontSize: 12 }} />
            <Text style={{ fontSize: token.fontSizeSM }}>{record.email}</Text>
          </div>
          {record.phone && (
            <div>
              <PhoneOutlined style={{ color: token.colorTextSecondary, marginRight: 4, fontSize: 12 }} />
              <Text style={{ fontSize: token.fontSizeSM }}>{record.phone}</Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: t('applications.columns.position'),
      key: 'position',
      width: 150,
      render: (_, record) => <Tag color="blue">{record.position}</Tag>,
      sorter: (a, b) => a.position.localeCompare(b.position),
    },
    {
      title: t('applications.columns.aiAssessment'),
      key: 'assessment',
      width: 180,
      render: (_, record) => {
        if (!record.summary) return <Text type="secondary">—</Text>;
        const score = Math.round(record.summary.final_score * 100);
        const tooltipContent = (
          <div style={{ lineHeight: 1.8 }}>
            <div><Text style={{ color: 'inherit' }}>{t('applications.columns.score')}: {score}%</Text></div>
            <div><Text style={{ color: 'inherit' }}>{t('applications.assessmentRec')}: {record.summary.recommendation_label}</Text></div>
            <div><Text style={{ color: 'inherit' }}>{t('applications.assessmentConf')}: {record.summary.confidence_level}</Text></div>
          </div>
        );
        return (
          <Tooltip title={tooltipContent}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'default' }}>
              <Progress
                percent={score}
                size="small"
                strokeColor={getScoreColor(token, score / 100)}
                showInfo={false}
                style={{ flex: 1, minWidth: 60, maxWidth: 100, margin: 0 }}
              />
              <Text strong style={{ fontSize: token.fontSizeSM, whiteSpace: 'nowrap' }}>{score}%</Text>
              <Link to={`${MODULE_BASE_ROUTE}/applications/${record.id}/summary`}>
                <Button
                  type="text"
                  icon={<SearchOutlined />}
                  size="small"
                  style={{ padding: '0 4px', height: 'auto', color: token.colorPrimary }}
                />
              </Link>
            </div>
          </Tooltip>
        );
      },
      sorter: (a, b) => (a.summary?.final_score ?? -1) - (b.summary?.final_score ?? -1),
    },
    {
      title: t('applications.columns.files'),
      key: 'files',
      width: 80,
      align: 'center',
      render: (_, record) => {
        const hasResume = record.resume && record.resume.length > 0;
        const hasAttachments = record.attachments && record.attachments.length > 0;
        if (!hasResume && !hasAttachments) return <Text type="secondary">—</Text>;
        return (
          <Space size={4}>
            {hasResume &&
              fileIconBtn(
                record.resume.length,
                token.colorPrimary,
                () => handleOpenResumeModal(record.resume),
                t('applications.columns.resume'),
              )}
            {hasAttachments &&
              fileIconBtn(
                record.attachments.length,
                token.colorSuccess,
                () => handleOpenAttachmentsModal(record.attachments),
                t('applications.columns.attachments'),
              )}
          </Space>
        );
      },
    },
    {
      title: t('applications.columns.createdAt'),
      key: 'created_at',
      width: 110,
      render: (_, record) => <DateTimeCell value={record.created_at} />,
      sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
      defaultSortOrder: 'descend',
    },
    {
      title: t('applications.columns.appointments'),
      key: 'appointments',
      width: 100,
      fixed: pinColumn('right'),
      align: 'center',
      render: (_, record) => {
        const hasAppointment = applicationsWithAppointments.has(record.id);

        return (
          <AppointmentScheduleButton
            hasAppointment={hasAppointment}
            scheduleLabel={t('applications.appointments.schedule')}
            detailsLabel={t('applications.appointments.details')}
            onSchedule={() => handleCreateAppointment(record)}
            onDetails={(event) => handleShowAppointmentDetails(record, event)}
          />
        );
      },
    },
    {
      title: t('applications.columns.actions'),
      key: 'actions',
      width: 100,
      fixed: pinColumn('right'),
      align: 'center',
      render: (_, record) => (
        <RowActionsMenu
          actions={[{ key: 'delete', label: t('applications.delete'), icon: <DeleteOutlined />, danger: true }]}
          onSelect={() => handleDeleteApplication(record)}
        />
      ),
    },
  ];

  return (
    <div style={{ padding: `${token.paddingSM}px ${token.paddingMD}px` }}>
      {error && (
        <ErrorAlert
          title={t('applications.errorLoading')}
          error={error}
          style={{ marginBottom: token.marginMD }}
        />
      )}
      
      {/* Search Input */}
      <div style={{ marginBottom: token.marginMD }}>
        <Input
          placeholder={t('applications.searchPlaceholder')}
          prefix={<SearchOutlined style={{ color: token.colorTextSecondary }} />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
          size="large"
          style={{
            maxWidth: 600
          }}
        />
        {searchText && (
          <Text type="secondary" style={{ display: 'block', marginTop: token.marginXS }}>
            {t('applications.foundResults', { count: filteredApplications.length })}
          </Text>
        )}
      </div>

      <Table
        columns={columns}
        dataSource={paginatedApplications}
        rowKey="id"
        loading={loading}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: totalCount,
          showQuickJumper: true,
          showTotal: (total, range) =>
            t('applications.paginationTotal', { start: range[0], end: range[1], total }),
          onChange: (newPage, size) => {
            handlePageChange(newPage, size);
          },
          onShowSizeChange: (current, size) => {
            handlePageChange(1, size);
          }
        }}
        size="middle"
      />

      {/* Resume Files Modal */}
      <FileListModal
        open={resumeModalOpen}
        onClose={() => {
          setResumeModalOpen(false);
          setSelectedResumeFiles([]);
        }}
        files={selectedResumeFiles}
        title={t('applications.resumeFiles', { count: selectedResumeFiles.length })}
      />

      {/* Attachments Files Modal */}
      <FileListModal
        open={attachmentsModalOpen}
        onClose={() => {
          setAttachmentsModalOpen(false);
          setSelectedAttachmentFiles([]);
        }}
        files={selectedAttachmentFiles}
        title={t('applications.attachmentFiles', { count: selectedAttachmentFiles.length })}
      />

    </div>
  );
}
